import { addDays, format } from "date-fns";
import { cn } from "@/lib/utils";

/**
 * The offsets on offer, in days from today.
 *
 * Not a prop: a field that wanted its own three would be the drift this
 * component exists to prevent. Four weeks is the last one deliberately — past
 * a month a date stops being a commitment and starts being a way of not
 * answering.
 */
const QUICK_PICKS = [
  { label: "Next week", days: 7 },
  { label: "2 weeks", days: 14 },
  { label: "4 weeks", days: 28 },
] as const;

interface DateQuickPicksProps {
  /** The ISO date (`yyyy-MM-dd`) currently chosen, so a matching chip reads as selected. */
  value: string | null;
  onChange: (value: string) => void;
  /** Follows the field the row belongs to — a live chip beside a frozen picker lies about what can still change. */
  disabled?: boolean;
  className?: string;
}

/**
 * A row of relative-date chips that sits under a {@link DatePicker}.
 *
 * Every offset is counted from today rather than from a date already chosen,
 * so tapping one twice lands on the same day instead of compounding — the
 * chips answer "when should this be done by", not "push it out again".
 *
 * Shared rather than duplicated per field: two surfaces offering the same
 * three offsets under drifting labels reads as two different features.
 */
export function DateQuickPicks({
  value,
  onChange,
  disabled = false,
  className,
}: DateQuickPicksProps) {
  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {QUICK_PICKS.map(({ label, days }) => {
        // Resolved at render, not at mount: a sheet left open across midnight
        // would otherwise offer dates counted from yesterday.
        const optionValue = format(addDays(new Date(), days), "yyyy-MM-dd");
        return (
          <button
            key={label}
            type="button"
            disabled={disabled}
            onClick={() => onChange(optionValue)}
            aria-pressed={value === optionValue}
            className={cn(
              "rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors",
              value === optionValue
                ? "border-foreground/20 bg-card shadow-sm"
                : "border-border bg-transparent text-muted-foreground",
              disabled && "opacity-50",
            )}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
